import type { SearchParams, SearchParamsType } from '$lib/services/urls/searchParams';
import type { Page } from '@sveltejs/kit';
import type { BoundaryCoordinates } from '../map/types';
import { ROUTE_ON_THE_WAY_POI, ROUTE_SEARCH_POI, ROUTE_ZOI_POI } from '$lib/constants/route';
import { isAnyPoiPartner } from '$lib/constants/sponsoredPoi';
import { getLanguageCode } from '$lib/i18n/language';
import { i18nPath } from '$lib/i18n/routing';
import { createBookingcomUrlWithLabel } from '$lib/services/bookingcom/urlWithLabel';
import { getPageNameWithCategory, isCorridorPageContext, isRoutePoiListPageContext, isZoiPageContext } from '$lib/services/urls/routeIds';
import { serializeSearchParams } from '$lib/services/urls/searchParams';
import {
	type AccommodationMichelinPoiData,
	type AccommodationPoiData,
	type DatasheetPoiData,
	type DatasheetPoiType,
	datasheetPoiTypeList,
	type DatasheetSideBarPoiData,
	type DatasheetSideBarPoiType,
	datasheetSideBarPoiTypeList,
	type HRTDatasheetPoiType,
	hrtDatasheetPoiTypeList,
	type MainPoiCategory,
	mainPoiCategoryList,
	type PoiCategory,
	type PoiData,
	type RestaurantMichelinPoiData,
	type RestaurantPoiData,
	type SummaryPoiData,
	type SummaryPoiType,
	summaryPoiTypeList,
	type TourismPoiData,
} from './types';

export function isAccommodationPoi(poi?: PoiData): poi is AccommodationPoiData {
	if (!poi) {
		return false;
	}
	return poi.__typename === 'AccommodationPOI';
}

export function isAccommodationMichelinPoi(poi?: PoiData): poi is AccommodationMichelinPoiData {
	if (!poi) {
		return false;
	}
	return poi.__typename === 'AccommodationMichelinPOI';
}

export function isRestaurantPoi(poi?: PoiData): poi is RestaurantPoiData {
	if (!poi) {
		return false;
	}
	return poi.__typename === 'RestaurantPOI';
}

export function isRestaurantMichelinPoi(poi?: PoiData): poi is RestaurantMichelinPoiData {
	if (!poi) {
		return false;
	}
	return poi.__typename === 'RestaurantMichelinPOI';
}

export function isTourismPoi(poi?: PoiData): poi is TourismPoiData {
	if (!poi) {
		return false;
	}
	return poi.__typename === 'TourismPOI';
}

export function isHRTPoi(poi?: PoiData): boolean {
	if (!poi) {
		return false;
	}
	return isAnyAccommodationPoi(poi) || isRestaurantMichelinPoi(poi) || isRestaurantPoi(poi) || isTourismPoi(poi);
}

export function isAnyAccommodationPoi(
	poi: PoiData,
): poi is AccommodationMichelinPoiData | AccommodationPoiData {
	return isAccommodationPoi(poi) || isAccommodationMichelinPoi(poi);
}

/** This function returns true if a poi is a "side bar" poi, i.e. a poi where a datasheet should be displayed on the side bar */
export function isDatasheetSideBarPoiData(poi?: PoiData): poi is DatasheetSideBarPoiData {
	if (!poi) {
		return false;
	}
	return datasheetSideBarPoiTypeList.includes(
		(poi as { __typename: DatasheetSideBarPoiType }).__typename,
	);
}

export function isDatasheetPoiData(poi?: PoiData): poi is DatasheetPoiData {
	if (!poi) {
		return false;
	}
	return datasheetPoiTypeList.includes((poi as { __typename: DatasheetPoiType }).__typename);
}

export function isHRTDatasheetPoiData(poi?: PoiData): poi is DatasheetPoiData {
	if (!poi) {
		return false;
	}
	return hrtDatasheetPoiTypeList.includes((poi as { __typename: HRTDatasheetPoiType }).__typename);
}

export function isSummaryPoiData(poi?: PoiData): poi is SummaryPoiData {
	if (!poi) {
		return false;
	}
	return summaryPoiTypeList.includes((poi as { __typename: SummaryPoiType }).__typename);
}

export function mapPoiToCategory(poi: PoiData): PoiCategory {
	switch (poi.__typename) {
		case 'AccommodationPOI':
		case 'AccommodationMichelinPOI':
			return 'ACCOMMODATION';
		case 'RestaurantPOI':
		case 'RestaurantMichelinPOI':
			return 'RESTAURANT';
		case 'TourismPOI':
			return 'TOURISM';
		case 'ParkingPOI':
			return 'PARKING';
		case 'ServiceGasStationPOI':
			return 'SERVICE_GAS_STATION';
		case 'ServiceEVPOI':
			return 'EV_SERVICE';
		case 'ServiceRestStopPOI':
			return 'REST_STOP';
		// To be modified with the new "service" poi category
		case 'ServicePOI':
			return 'EV_SERVICE';
		case 'PartnerPOI':
			return 'PARTNER';
		case 'Partner2POI':
			return 'PARTNER2';
	}
}

/** This function returns bounds (quite) centered on a poi */
export function getBoundsFromPoi(poi: PoiData): BoundaryCoordinates {
	// Due to Mercator projection, the size of a degree of longitude depends on the latitude
	// However the difference is small enough to be ignored here
	// So, we use a constant offset of 0.05 degrees that will be added or substracted to the center of the poi to get the bounds NE and SW corners
	const OFFSET = 0.05;
	return [
		[poi.coordinates.lng - OFFSET, poi.coordinates.lat - OFFSET],
		[poi.coordinates.lng + OFFSET, poi.coordinates.lat + OFFSET],
	];
}

export function isMainPoiCategory(poi: PoiCategory): poi is MainPoiCategory {
	return mainPoiCategoryList.includes(poi as MainPoiCategory);
}

export function getBookUrl(
	poi: DatasheetPoiData,
	page: Page,
	searchParams: SearchParamsType,
) {
	if (isAccommodationPoi(poi)) {
		const { pagename, waypointsInfo } = page.data;

		if (pagename?.toLowerCase().includes('datasheet')) {
			return createBookingcomUrlWithLabel().poi_in_datasheet({
				poi,
				searchParams: searchParams as SearchParams,
				arrival: waypointsInfo?.arrival.name,
			});
		} else {
			return createBookingcomUrlWithLabel().poi_in_poilist({
				poi,
				searchParams: searchParams as SearchParams,
				arrival: waypointsInfo?.arrival.name,
			});
		}
	}

	if (isAccommodationMichelinPoi(poi)) {
		const url = decodeURIComponent(poi.bookUrl);
		const pageName = getPageNameWithCategory(page, searchParams);

		return pageName ? url.replace('{{pageName}}', pageName) : url;
	}

	if (isAnyPoiPartner(poi)) {
		return poi.bookUrl;
	}
}

export function getPoiDatasheetRoute(route: { id: string | null }, poi: PoiData) {
	if (isZoiPageContext(route)) {
		return ROUTE_ZOI_POI;
	}

	if (isRoutePoiListPageContext(route)) {
		return ROUTE_SEARCH_POI;
	}

	if (isCorridorPageContext(route)) {
		return ROUTE_ON_THE_WAY_POI;
	}

	return `/map/${mapPoiToCategory(poi)}/poi/${poi.urlIdentifier}`;
}

export function getPoiDetailLink(poi: PoiData, searchParams: SearchParamsType, datasheetRoute: string, withSearchParams: boolean = true) {
	const params = withSearchParams ? serializeSearchParams({ ...searchParams }) : '';

	if (isDatasheetPoiData(poi)) {
		return i18nPath(`${datasheetRoute}?${params}`, getLanguageCode());
	}

	return `?${params}`;
}
